import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../utils/authStorage';
import { showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import HeaderInfo from '../../../../Components/Common/HeaderInfo';
import Remarks, { validateRemarksSection } from './Remarks';
import Remarks__list from './Remarks__list';
import { filterRemarksList } from '../../../../Custom/Custom';
import { refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';

function FinalCheckList() {
  const navigate = useNavigate();

  // State for header data
  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  // State for checklist items
  const [checklistData, setChecklistData] = useState([]);

  // Use ref to track if data has been loaded to prevent multiple API calls
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);

  // State for Remarks section
  const [remarksData, setRemarksData] = useState({
    remarksBlock: '',
    remarksItems: '',
    remarks: ''
  });
  const [remarksList, setRemarksList] = useState([]);

  // Function to fetch checklist data from API
  const fetchChecklistData = useCallback(async (dslNo, surveyYear) => {
    if (!dslNo || !surveyYear) {
      console.log('DSL No or Survey Year not available for fetching checklist data');
      dataLoadedRef.current = true;
      return;
    }

    console.log('Starting API call for AsiBalanceFinalChecklist with DSL:', dslNo, 'Year:', surveyYear);
    showLoading(true);
    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        console.error('Authentication token not found for fetching checklist data');
        showLoading(false);
        dataLoadedRef.current = true;
        return;
      }

      // Extract only year part (e.g., "2024" from "2024-25")
      const yearOnly = surveyYear ? surveyYear.split('-')[0] : surveyYear;

      const payload = {
        dsl_no: dslNo,
        survey_year: yearOnly
      };

      console.log('Fetching checklist data with payload:', payload);

      const response = await axios.post(`${API_URL}api/input-items-indigenous/AsiBalanceFinalChecklist`, payload, currentConfig);

      if (response.data && response.data.status && Array.isArray(response.data.data)) {
        console.log('Checklist data fetched successfully:', response.data.data);

        // Map API data to checklist format (SL No. = frontend serial 1,2,3...; checkPointId = API id for save)
        const mappedChecklistData = response.data.data.map((item, index) => {
          let observationValue = false;
          if (item.observation !== null && item.observation !== undefined) {
            const obsStr = String(item.observation).trim();
            observationValue = obsStr.toLowerCase() === 'true' || obsStr === '1';
          }
          return {
            checkPointId: item.id != null ? item.id : index + 1,
            slNo: index + 1,
            checkPoint: item.check_points || item.checkPoint || '',
            observation: observationValue
          };
        });

        setChecklistData(mappedChecklistData);

        // Map remarks data from API response if available
        let mappedRemarksList = [];
        if (response.data.remarks && Array.isArray(response.data.remarks)) {
          mappedRemarksList = response.data.remarks.map((remark, index) => {
            // Format date for display
            let formattedDate = '';
            const dateValue = remark.createdDate || remark.created_date || remark.createdDate || remark.updated_date || remark.updated_time || remark.created_time || '';
            if (dateValue) {
              try {
                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                  formattedDate = date.toLocaleString('en-IN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                }
              } catch (e) {
                formattedDate = dateValue;
              }
            }

            return {
              slNo: index + 1,
              blocks: remark.block || '',
              columns: remark.columnsName || remark.columns_name || remark.columns || '',
              items: remark.items || remark.Items || '',
              remarks: remark.remarks || '',
              remarksCreatedOn: formattedDate || dateValue || '',
              remarksBy: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || '',
              role: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || ''
            };
          });
          setRemarksList(mappedRemarksList);
          console.log('Mapped remarks list:', mappedRemarksList);
        } else {
          setRemarksList([]);
        }

        // Set default remarksData - Block to "ALL" by default to show all remarks
        setRemarksData({
          remarksBlock: '',
          remarksItems: '',
          remarks: ''
        });

        dataLoadedRef.current = true;
        console.log('Checklist data loaded successfully');
      } else {
        console.log('No checklist data found or invalid response');
        setChecklistData([]);
        setRemarksList([]);
        dataLoadedRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching checklist data from API:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 404) {
        console.log('No existing checklist data found, initializing with empty array');
        setChecklistData([]);
      } else {
        toast.error('Failed to load checklist data.', messages.COLOR_RED);
        setChecklistData([]);
      }
      dataLoadedRef.current = true;
    } finally {
      showLoading(false);
    }
  }, []);

  // Fetch header data from localStorage when component mounts
  useEffect(() => {
    const fetchHeaderData = async () => {
      if (dataLoadedRef.current || isLoadingRef.current || apiCallMadeRef.current) {
        console.log('Data already loaded, loading in progress, or API call already made - skipping');
        return;
      }

      try {
        const balanceSheetData = localStorage.getItem('balanceSheetFormData');

        if (balanceSheetData) {
          const parsedData = JSON.parse(balanceSheetData);

          const headerInfo = {
            despatchSlNo: parsedData.dslNo || '',
            permanentSlNo: parsedData.pslNo || '',
            companyName: parsedData.nameUnit || '',
            dslNo: parsedData.dslNo || '',
            surveyYear: parsedData.year || ''
          };

          setHeaderData(headerInfo);

          // Fetch checklist data if DSL No and Survey Year are available
          if (headerInfo.dslNo && headerInfo.surveyYear) {
            apiCallMadeRef.current = true; 
            isLoadingRef.current = true;
            await fetchChecklistData(headerInfo.dslNo, headerInfo.surveyYear);
            isLoadingRef.current = false;
          } else {
            setChecklistData([]);
          }
        } else {
          console.log('No balanceSheet data found in localStorage');
          setHeaderData({
            despatchSlNo: 'DSL No. not set',
            permanentSlNo: 'PSL No. not set',
            companyName: 'Company name not set',
            dslNo: '',
            surveyYear: ''
          });
          setChecklistData([]);
        }
      } catch (error) {
        console.error('Error fetching data from localStorage:', error);
        setHeaderData({
          despatchSlNo: 'Error loading data',
          permanentSlNo: 'Error loading data',
          companyName: 'Error loading data',
          dslNo: '',
          surveyYear: ''
        });
        setChecklistData([]);
      }
    };

    fetchHeaderData();
  }, [fetchChecklistData]);

  const handleCheckboxChange = (checkPointId) => {
    setChecklistData(prevData =>
      prevData.map(item =>
        item.checkPointId === checkPointId ? { ...item, observation: !item.observation } : item
      )
    );
  };

  // Get selected Item Descriptions from checklist for Remarks Items dropdown
  const selectedItemDescriptions = useMemo(() => {
    return checklistData.map(item => `Check Point ${item.slNo}: ${item.checkPoint.substring(0, 50)}...`).filter(item => item !== '');
  }, [checklistData]);

  const getAuthConfig = () => {
    if (!hasAuthSession()) return null;
    return { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Cache-Control': 'no-cache' } };
  };

  // Handle reset function
  const handleReset = () => {
    setChecklistData(prevData =>
      prevData.map(item => ({
        ...item,
        observation: false
      }))
    );
    setRemarksData({
      remarksBlock: '',
      remarksItems: '',
      remarks: ''
    });
  };

  // Function to save data to API
  const handleSaveToAPI = async () => {
    const remarksValidation = validateRemarksSection(remarksData);
    if (!remarksValidation.valid) {
      toast.error(remarksValidation.message, messages.COLOR_RED);
      return false;
    }

    if (!headerData.dslNo || headerData.dslNo === 'DSL No. not set') {
      toast.error('Please ensure DSL No. is available', messages.COLOR_RED);
      return false;
    }

    showLoading(true);

    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Extract year only from survey year (e.g., "2024-25" -> "2024")
      const yearOnly = headerData.surveyYear ? headerData.surveyYear.split('-')[0] : headerData.surveyYear || '2024';

      // Prepare checklist data for API
      const dataToSave = checklistData.map(item => ({
        dsl_no: headerData.dslNo,
        survey_year: yearOnly,
        check_point_id: item.checkPointId,
        check_point: item.checkPoint,
        observation: item.observation
      }));

      // Prepare remarks array
      const remarksArray = [];
      if (remarksData.remarksBlock || remarksData.remarksItems || remarksData.remarks) {
        remarksArray.push({
          dslNo: headerData.dslNo.toString(),
          surveyYear: yearOnly,
          block: remarksData.remarksBlock || '',
          columnsName: remarksData.remarksItems || '',
          items: remarksData.remarksBlock || '',
          remarks: remarksData.remarks || ''
        });
      }

      // Structure payload with data and remarks arrays
      const finalPayload = {
        data: dataToSave,
        remarks: remarksArray
      };

      console.log('Saving Final Checklist data to API:', finalPayload);

      // TODO: Update API endpoint when available
      const response = await axios.post(`${API_URL}api/input-items-indigenous/AsiBalanceFinalChecklist`, finalPayload, currentConfig);

      if (response.data && response.data.status) {
        toast.success('Data saved successfully!', messages.COLOR_RED);
        // Refresh balance sheet validation to update header colors
        if (headerData.dslNo && headerData.surveyYear) {
          await refreshBalanceSheetValidation(headerData.dslNo, headerData.surveyYear);
        }
        return true;
      } else {
        toast.error('Failed to save data. Please check the data and try again.', messages.COLOR_RED);
        return false;
      }

    } catch (error) {
      console.error('API Error Details:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions or contact administrator.', messages.COLOR_RED);
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found (404). Please check the URL.', messages.COLOR_RED);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.', messages.COLOR_RED);
      } else {
        toast.error(`Error ${error.response?.status || 'Unknown'}: ${error.message || 'An error occurred'}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      showLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      {/* Header Section */}
      <HeaderInfo
        title="Final Checklist"
        icon="fa fa-check-circle"
        iconColor="#1976d2"
        titleColor="#1976d2"
        backgroundColor=""
        dispatchSlNo={headerData.despatchSlNo}
        permanentSlNo={headerData.permanentSlNo}
        companyName={headerData.companyName}
        companyPlaceholder="Enter Company Name"
        showBlockCType={false}
        includeTableStyles={false}
      />

      {/* Main Content Area */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            <div className="col-12">
              <div className="table-responsive">
                <table className="table table-bordered mb-0">
                  <thead>
                    <tr>
                      <th>SL No.</th>
                      <th>Check Points</th>
                      <th>Observation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checklistData.map((item) => (
                      <tr key={item.checkPointId}>
                        <td style={{ textAlign: 'center', width: '80px' }}>{item.slNo}</td>
                        <td style={{ padding: '12px' }}>{item.checkPoint}</td>
                        <td style={{ textAlign: 'center', width: '120px' }}>
                          <input
                            type="checkbox"
                            checked={item.observation}
                            onChange={() => handleCheckboxChange(item.checkPointId)}
                            style={{
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer'
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Remarks Section */}
      <div className='row'>
        <div className='col-md-12 remarks-section'>
          <Remarks
            remarksData={remarksData}
            setRemarksData={setRemarksData}
            blockItemData={[]}
            filteredBlockItems={[]}
            isLoadingBlockItems={false}
            debouncedBlockItemSearch={() => { }}
            loadMoreBlockItemData={() => { }}
            hasMoreBlockItems={false}
            isLoadingMoreBlockItems={false}
            selectedItemsFromTable={selectedItemDescriptions}
            remarksList={remarksList}
            availableColumns={[1, 2, 3]}
          />
        </div>
      </div>


      {/* Action Buttons */}
      <div className="row">
        <div className="col-12">
          <hr />
        </div>
        <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
          <button
            type="button"
            className="btn btn_orange mx-1"
          >
            Print
            <i className="fa fa-print ms-1"></i>
          </button>
          <button
            type="button"
            className="btn btn_orange mx-1"
            onClick={handleSaveToAPI}
          >
            Save
          </button>
          <button
            type="reset"
            className="btn btn_orange mx-1"
            onClick={handleReset}
          >
            Reset
          </button>
          <button
            type="button"
            className="btn btn_orange mx-1"
            onClick={() => navigate('/asiScheduleMainMenu')}
          >
            Cancel
          </button>
        </div>
        <div className="col-md-4 text-center text-md-end">
          <button
            type="button"
            className="btn btn_orange mx-1"
            onClick={() => navigate('/net-profit-tally')}
          >
            <i className="fa fa-angle-left me-1"></i>
            Previous
          </button>
          <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/net-profit-tally')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
         
        </div>
      </div>

      {/* Remarks List Section */}
      {(() => {
        const filteredRemarksList = filterRemarksList(remarksList, remarksData);
        return <div className=''><Remarks__list remarksData={filteredRemarksList} /></div>;
      })()}

      

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
    </div>
  );
}

export default FinalCheckList;